/**
 * admin-journals.ts - 일지(Journal) 관련 Firestore 조회 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/journals/{journalId}
 */

import { collection, query, where, getDocs, doc, getDoc, type Timestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import type {
  EquipmentType,
  ExerciseEntry,
} from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { ClassType } from "@vlab-corporation/vlab-pilates-service-interface/branch";

export interface AdminJournalDocument {
  id: string;
  classId: string;
  instructorId: string;
  /** @deprecated studiomateMemberId를 사용하세요. 하위 호환용으로만 유지됩니다. */
  memberId?: string;
  /** Studiomate 회원 고유 ID (숫자). 회원 상세 페이지 링크 생성에 사용된다. */
  studiomateMemberId?: number;
  classType?: ClassType;
  isOt?: boolean;
  equipments: EquipmentType[];
  exercises: ExerciseEntry[];
  problemList: string;
  notes?: string;
  bodyAnalysis?: string;
  memberGoal?: string;
  instructorMemo?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * 특정 강사의 일지 목록을 Firestore에서 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param instructorId - 강사 ID
 * @returns 일지 문서 배열 (createdAt 내림차순)
 */
export async function getJournalsByInstructor(
  ownerUid: string,
  centerId: number,
  instructorId: string,
): Promise<AdminJournalDocument[]> {
  const col = collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "journals",
  );
  const q = query(col, where("instructorId", "==", instructorId));
  const snap = await getDocs(q);

  const docs: AdminJournalDocument[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<AdminJournalDocument, "id">),
  }));

  // createdAt 내림차순 정렬 (최신순)
  docs.sort((a, b) => {
    const aTime = a.createdAt?.toMillis?.() ?? 0;
    const bTime = b.createdAt?.toMillis?.() ?? 0;
    return bTime - aTime;
  });

  return docs;
}

/**
 * 특정 일지를 ID로 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param journalId - 일지 ID
 * @returns 일지 문서 또는 null
 */
export async function getJournalById(
  ownerUid: string,
  centerId: number,
  journalId: string,
): Promise<AdminJournalDocument | null> {
  const ref = doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "journals",
    journalId,
  );
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...(snap.data() as Omit<AdminJournalDocument, "id">),
  };
}
